import React, {useMemo, useState} from 'react';
import {useHistory, useParams} from 'react-router-dom';
import {useQuery} from 'react-query';
import {Box, Button, LinearProgress, Paper, Typography} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {DataGridPro} from '@mui/x-data-grid-pro';
import Canvas from '../../navigation/canvas/Canvas';
import ErrorPopup from '../../common/ErrorPopup';
import PenHealthService from '../../../apis/PenHealthService';
import {describeError, usePenHealthSummary} from './PenHealth';
import {buildFlagColumns, MultiSiteParentTag, StaleTag} from './columns';

const PenHealthDetail = () => {
  const history = useHistory();
  const {businessId} = useParams();
  const [errDisplayText, setErrDisplayText] = useState('');
  const [errorOpen, setErrorOpen] = useState(false);

  const handleError = (error) => {
    setErrDisplayText(describeError(error));
    setErrorOpen(true);
  };

  const summary = usePenHealthSummary(handleError);
  const flags = useQuery(['getPenHealthFlags', businessId], () => PenHealthService.getFlags(businessId), {
    onError: handleError,
    retry: false
  });

  const business = (summary.data || []).find((b) => b.business_id === businessId);
  const columns = useMemo(buildFlagColumns, []);

  return (
    <Box>
      <Canvas appName='nGEST' title={business ? `Pen Health: ${business.business_name}` : 'Pen Health'}>
        <Box sx={{mb: '1rem', display: 'flex', alignItems: 'center', gap: '1rem'}}>
          <Button startIcon={<ArrowBackIcon/>} onClick={() => history.push('/pen-health')}>All businesses</Button>
          {business && (
            <Typography variant='body2' sx={{color: 'text.secondary'}}>
              Owner {business.owner_id} · {Number(business.cow_count).toLocaleString()} cows · herd snapshot {business.snapshot_date}
            </Typography>
          )}
          {business?.is_multi_site_parent && <MultiSiteParentTag/>}
          {business?.multi_site_owner_id && !business.is_multi_site_parent && (
            <Typography variant='body2' sx={{color: 'text.secondary'}}>Site of {business.multi_site_name}</Typography>
          )}
          {business?.is_stale && <StaleTag/>}
        </Box>
        <Paper sx={{height: '68vh'}}>
          <DataGridPro
            columns={columns}
            rows={flags.data || []}
            getRowId={(row) => `${row.src_pen_id}|${row.category}`}
            loading={flags.isLoading}
            slots={{loadingOverlay: LinearProgress}}
            getRowHeight={() => 'auto'}
            disableRowSelectionOnClick
            localeText={{noRowsLabel: 'No flagged pens'}}
            sx={{'& .pen-health-muted': {color: 'text.secondary'}}}
          />
        </Paper>
      </Canvas>
      <ErrorPopup open={errorOpen} setOpen={setErrorOpen} displayText={errDisplayText}/>
    </Box>
  );
};

export default PenHealthDetail;
